/* ============================================================
   BOOST — popup profil / apply program (pelanggan)
   ============================================================
   Dua guna, satu borang:
     · program = null  → "Edit profile" sahaja (aksi simpanProfil)
     · program = {...} → apply program itu (aksi mohon: profil + tick setuju)
   Semakan sebenar di SERVER (functions/boostTeras.js) — di sini hanya
   tanda "wajib" ikut platform supaya pelanggan tahu apa diminta. Ralat
   server dipapar ikut kod (kamus boost.err.*), tidak ditelan.
   ============================================================ */
import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';

import '../design/tokens.css';
import { Btn } from '../design/ui';
import { useT } from '../i18n';
import { panggilBoost, mesejRalatPelanggan, JENIS } from '../services/boostService';

export default function BoostMohonModal({ program, mcnNama, emel, profilAwal, onTutup, onSiap }) {
    const t = useT();
    const platform = program ? JENIS[program.jenis]?.platform : null;
    const [p, setP] = useState({
        nama: profilAwal?.nama || '', wa: profilAwal?.wa || '',
        tiktok1: profilAwal?.tiktok1 || '', tiktok2: profilAwal?.tiktok2 || '', shopee: profilAwal?.shopee || '',
    });
    const [setuju, setSetuju] = useState(false);
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');

    const ubah = (k) => (e) => { setP({ ...p, [k]: e.target.value }); setRalat(''); };
    const tutup = () => { if (!sibuk) onTutup(); };

    async function hantar(e) {
        e.preventDefault();
        if (program && !setuju) { setRalat(t('boost.err.perlu-setuju')); return; }
        setSibuk(true);
        setRalat('');
        try {
            const hasil = program
                ? await panggilBoost('mohon', { programId: program.id, setuju: true, profil: p })
                : await panggilBoost('simpanProfil', { profil: p });
            onSiap(hasil);
        } catch (err) {
            setRalat(mesejRalatPelanggan(err, t, { mcn: mcnNama || '' }));
            setSibuk(false);
        }
    }

    const tandaWajib = (wajib) => (
        <span className="s-dim" style={{ fontWeight: 400, fontSize: 12 }}>
            {wajib ? `· ${t('boost.form.required')}` : `· ${t('boost.form.optional')}`}
        </span>
    );
    const medan = (id, label, kunci, { ph, wajib, hint, mod } = {}) => (
        <div style={{ marginBottom: 14 }}>
            <label className="s-lbl" htmlFor={id}>{label} {wajib != null && tandaWajib(wajib)}</label>
            <input id={id} className="s-input" placeholder={ph} value={p[kunci]} onChange={ubah(kunci)}
                inputMode={mod} autoComplete="off" disabled={sibuk} />
            {hint && <p className="s-hint">{hint}</p>}
        </div>
    );

    const tajuk = program
        ? t('boost.apply.title', { mcn: mcnNama, program: JENIS[program.jenis]?.label || '' })
        : t('boost.profile.title');

    return (
        <div className="s-root s-modal" role="dialog" aria-modal="true" aria-label={tajuk} onClick={tutup}>
            <form className="s-modal__panel" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()} onSubmit={hantar}>
                <header className="s-modal__head">
                    <h2 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>{tajuk}</h2>
                    <button type="button" className="s-modal__x" aria-label={t('boost.cancel')} onClick={tutup}>
                        <X size={15} strokeWidth={2} />
                    </button>
                </header>

                <div style={{ padding: 16, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    <p className="s-dim" style={{ margin: '0 0 16px', lineHeight: 1.55 }}>
                        {program ? t('boost.apply.desc', { mcn: mcnNama }) : t('boost.profile.desc')}
                    </p>

                    {medan('bo-nama', t('boost.form.name'), 'nama', { ph: t('boost.form.namePh') })}

                    <div style={{ marginBottom: 14 }}>
                        <label className="s-lbl" htmlFor="bo-emel">{t('boost.form.email')}</label>
                        <input id="bo-emel" className="s-input" value={emel || ''} disabled readOnly />
                        <p className="s-hint">{t('boost.form.emailHint')}</p>
                    </div>

                    {medan('bo-wa', t('boost.form.wa'), 'wa', { ph: '0123456789', mod: 'tel', hint: t('boost.form.waHint') })}
                    {medan('bo-tt1', t('boost.form.tiktok'), 'tiktok1', { ph: '@username', wajib: platform ? platform === 'tiktok' : null })}
                    {medan('bo-tt2', t('boost.form.tiktok2'), 'tiktok2', { ph: '@username', wajib: false })}
                    {medan('bo-shp', t('boost.form.shopee'), 'shopee', { ph: 'username', wajib: platform ? platform === 'shopee' : null })}

                    {program && JENIS[program.jenis]?.bind && (
                        <p className="s-hint" style={{ marginTop: -4, marginBottom: 14 }}>
                            {t('boost.apply.bindNote', { platform: platform === 'shopee' ? 'Shopee' : 'TikTok' })}
                        </p>
                    )}

                    {program ? (
                        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13, color: 'var(--s-ink-2)', lineHeight: 1.5 }}>
                            <input type="checkbox" checked={setuju} disabled={sibuk}
                                onChange={(e) => { setSetuju(e.target.checked); setRalat(''); }}
                                style={{ marginTop: 3, flex: 'none' }} />
                            <span>{t('boost.apply.consent', { mcn: mcnNama })}</span>
                        </label>
                    ) : (
                        <p className="s-hint">{t('boost.profile.note')}</p>
                    )}

                </div>

                <footer className="s-modal__foot" style={{ justifyContent: 'flex-end' }}>
                    {/* Ralat di KAKI popup (sentiasa kelihatan) — dalam badan ia tersorok di bawah lipatan (dilihat 4 Nov). */}
                    {ralat && (
                        <p role="alert" style={{ flex: '1 1 220px', margin: 0, fontSize: 13, color: 'var(--s-stop)', lineHeight: 1.45 }}>
                            {ralat}
                        </p>
                    )}
                    <Btn variant="ghost" onClick={tutup} disabled={sibuk}>{t('boost.cancel')}</Btn>
                    <Btn type="submit" disabled={sibuk}
                        icon={sibuk ? <Loader2 size={14} strokeWidth={2} className="animate-spin" /> : null}>
                        {sibuk ? t('boost.apply.sending') : program ? t('boost.apply.submit') : t('boost.profile.save')}
                    </Btn>
                </footer>
            </form>
        </div>
    );
}
